import {
  boolean,
  date,
  decimal,
  pgEnum,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
  varchar,
} from "drizzle-orm/pg-core"
import { assets, liabilities } from "./asset"
import { companies } from "./company"
import { contacts, locations } from "./contact"
import { documents } from "./document"
import { estates } from "./estate"
import { trusts } from "./trust"

// Purpose: Defines types of insurance.
export const insuranceTypes = pgTable("insurance_type", {
  id: uuid("id").primaryKey().defaultRandom(),
  name: varchar("name", { length: 100 }).notNull().unique(),
})

// enum
export const insuranceTypeEnum = pgEnum("insurance_type_enum", [
  "TERM_LIFE_INSURANCE",
  "WHOLE_LIFE_INSURANCE",
  "UNIVERSAL_LIFE_INSURANCE",
  "VARIABLE_LIFE_INSURANCE",
  "INDEXED_UNIVERSAL_LIFE_INSURANCE",
  "VARIABLE_UNIVERSAL_LIFE_INSURANCE",
  "DISABILITY_INSURANCE",
  "CRITICAL_ILLNESS",
  "HEALTH_INSURANCE",
  "LONG-TERM_CARE_INSURANCE",
  "BUSINESS_INSURANCE",
  "OTHER",
  "NONE",
])

export const insuranceCategoryEnum = pgEnum("insurance_category_enum", [
  "INDIVIDUAL",
  "GROUP",
  "CORPORATE",
])

export const insuranceBenefitsTypeEnum = pgEnum("insurance_benefits_type_enum", [
  "DEATH_BENEFIT",
  "CASH_VALUE",
  "ACCELERATED_DEATH_BENEFIT_RIDER",
  "WAIVER_OF_PREMIUM_RIDER",
  "LONG_TERM_CARE_RIDER",
  "ACCIDENTAL_DEATH_BENEFIT_RIDER",
  "CHRONIC_ILLNESS_RIDER",
  "CRITICAL_ILLNESS_RIDER",
])

export const insuranceOwnershipTypeEnum = pgEnum("insurance_ownership_type_enum", [
  "INDIVIDUAL",
  "CORPORATE",
  "GROUP",
])

export const premiumFrequencyEnum = pgEnum("premium_frequency_enum", [
  "MONTHLY",
  "QUARTERLY",
  "SEMI_ANNUALLY",
  "ANNUALLY",
  "SINGLE_PREMIUM",
  "OTHER",
])

export const insuranceContactRoleEnum = pgEnum("insurance_contact_role_enum", [
  "INSURED",
  "OWNER",
  "CO_OWNER",
  "AGENT",
  "ADVISOR",
  "OTHER",
])

export const insuranceContacts = pgTable("insurance_contact", {
  id: uuid("id").primaryKey().defaultRandom(),
  insuranceId: uuid("insurance_id")
    .notNull()
    .references(() => insurances.id, { onDelete: "cascade" }),
  contactId: uuid("contact_id")
    .notNull()
    .references(() => contacts.id, { onDelete: "cascade" }),
  percentageShare: decimal("percentage_share", { precision: 5, scale: 2 }),
  role: insuranceContactRoleEnum("role"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
})

// Junction table: Insurance ↔ Assets (multiple assets per insurance)
export const insuranceAssets = pgTable("insurance_asset", {
  id: uuid("id").primaryKey().defaultRandom(),
  insuranceId: uuid("insurance_id")
    .notNull()
    .references(() => insurances.id, { onDelete: "cascade" }),
  assetId: uuid("asset_id")
    .notNull()
    .references(() => assets.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
})

// Purpose: Links insurances to documents
export const insuranceDocuments = pgTable("insurance_document", {
  id: uuid("id").primaryKey().defaultRandom(),
  insuranceId: uuid("insurance_id")
    .notNull()
    .references(() => insurances.id, { onDelete: "cascade" }),
  documentId: uuid("document_id")
    .notNull()
    .references(() => documents.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
})

// Purpose: Stores individual benefit records for insurance policies with their values
export const insuranceBenefits = pgTable("insurance_benefit", {
  id: uuid("id").primaryKey().defaultRandom(),
  insuranceId: uuid("insurance_id")
    .notNull()
    .references(() => insurances.id, { onDelete: "cascade" }),
  benefitType: insuranceBenefitsTypeEnum("benefit_type").notNull(),
  benefitValue: decimal("benefit_value", { precision: 18, scale: 2 }).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
})

// Purpose: Stores details about insurance policies relevant to an estate.
export const insurances = pgTable(
  "insurance",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    estateId: uuid("estate_id")
      .notNull()
      .references(() => estates.id, { onDelete: "cascade" }),
    insuranceType: insuranceTypeEnum("insurance_type_enum"),
    insuranceCategory: insuranceCategoryEnum("insurance_category_enum"),
    insuranceBenefitsTypes: insuranceBenefitsTypeEnum("insurance_benefits_types").array(),
    insuranceOwnershipType: insuranceOwnershipTypeEnum("insurance_ownership_type_enum"),
    policyNumber: varchar("policy_number", { length: 255 }).notNull(),
    providerContactId: uuid("provider_contact_id").references(() => contacts.id, {
      onDelete: "cascade",
    }),
    provider: text("provider"),
    insuredContactId: uuid("insured_contact_id").references(() => contacts.id, {
      onDelete: "cascade",
    }),
    ownerContactId: uuid("owner_contact_id").references(() => contacts.id, {
      onDelete: "set null",
    }),
    ownerTrustId: uuid("owner_trust_id").references(() => trusts.id, { onDelete: "set null" }),
    ownerCompanyId: uuid("owner_company_id").references(() => companies.id, {
      onDelete: "set null",
    }),
    policyDetails: text("policy_details"),
    coverageDetails: text("coverage_details"),
    coverageAmount: decimal("coverage_amount", { precision: 18, scale: 2 }),
    premiumAmount: decimal("premium_amount", { precision: 18, scale: 2 }),
    premiumFrequency: premiumFrequencyEnum("premium_frequency_enum"),
    startDate: date("start_date"),
    endDate: date("end_date"),
    notes: text("notes"),
    locationId: uuid("location_id").references(() => locations.id, { onDelete: "set null" }),
    insuranceLiabilityId: uuid("insurance_liability_id").references(() => liabilities.id, {
      onDelete: "set null",
    }),
    isDraft: boolean("is_draft").default(false).notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    // Note: For 'ON UPDATE CURRENT_TIMESTAMP' functionality in PostgreSQL,
    // a database trigger is required. This schema sets the timestamp on creation,
    // and application logic or a trigger should handle updates.
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [
    uniqueIndex("insurance_estate_policy_number_uq").on(table.estateId, table.policyNumber),
  ],
)
